// User memory system with robust persistence and fallback

const STORAGE_KEY = "decision-os-user-memory";
const BACKUP_KEY = "decision-os-user-memory-backup";

export interface UserMemory {
  country?: string;
  region?: string;
  currency?: string;
  preferredPriority?: string; // price | speed | quality | safety
  localPreference?: "local" | "international" | "adaptive";
  queriesCount: number;
  lastCategories: string[];
  lastUpdated: string;
  // Expanded profile data
  preferredBrands?: string[];
  avoidBrands?: string[];
  budgetPreference?: "budget" | "mid" | "premium" | "any";
  deliveryPreference?: "fastest" | "standard" | "flexible";
  lastSearches?: string[];
  sessionId?: string;
  // Use case tracking
  primaryUseCase?: string;
  useCaseHistory?: string[];
  // Geo memory
  detectedCountry?: string;
  detectedCity?: string;
  // Decision quality tracking
  totalDecisions: number;
  clarificationsSkipped: number;
}

function getDefault(): UserMemory {
  return {
    queriesCount: 0,
    totalDecisions: 0,
    clarificationsSkipped: 0,
    lastCategories: [],
    lastUpdated: new Date().toISOString(),
    lastSearches: [],
    useCaseHistory: [],
    sessionId: `session-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  };
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function loadMemory(): UserMemory {
  const raw = readStorage(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      return { ...getDefault(), ...parsed };
    } catch {}
  }

  const backup = readStorage(BACKUP_KEY);
  if (backup) {
    try {
      const parsed = JSON.parse(backup);
      writeStorage(STORAGE_KEY, backup);
      return { ...getDefault(), ...parsed };
    } catch {}
  }

  return getDefault();
}

export function saveMemory(mem: UserMemory): void {
  mem.lastUpdated = new Date().toISOString();
  const serialized = JSON.stringify(mem);
  writeStorage(STORAGE_KEY, serialized);
  writeStorage(BACKUP_KEY, serialized);
}

export function updateMemoryFromQuery(
  country?: string,
  region?: string,
  currency?: string,
  category?: string,
  priority?: string,
  localPref?: "local" | "international" | "adaptive",
  useCase?: string
): UserMemory {
  const mem = loadMemory();
  mem.queriesCount++;
  mem.totalDecisions++;

  if (country) mem.country = country;
  if (region) mem.region = region;
  if (currency) mem.currency = currency;
  if (priority) mem.preferredPriority = priority;
  if (localPref) mem.localPreference = localPref;

  if (category && category !== "general") {
    mem.lastCategories = [category, ...mem.lastCategories.filter((c) => c !== category)].slice(0, 5);
  }

  // Track use case patterns
  if (useCase) {
    if (!mem.useCaseHistory) mem.useCaseHistory = [];
    mem.useCaseHistory = [useCase, ...mem.useCaseHistory.filter(u => u !== useCase)].slice(0, 10);
    // Set primary use case from most frequent
    const freq: Record<string, number> = {};
    mem.useCaseHistory.forEach(u => freq[u] = (freq[u] || 0) + 1);
    mem.primaryUseCase = Object.entries(freq).sort((a, b) => b[1] - a[1])[0]?.[0];
  }

  // Infer budget preference from repeated queries
  if (priority === "value" || priority === "price") {
    mem.budgetPreference = mem.budgetPreference === "budget" ? "budget" : "mid";
  }
  if (priority === "quality" || priority === "premium quality") {
    mem.budgetPreference = "premium";
  }

  // Infer delivery preference
  if (priority === "speed" || priority === "fast") {
    mem.deliveryPreference = "fastest";
  }

  saveMemory(mem);
  return mem;
}

export function addSearchToMemory(query: string): void {
  const mem = loadMemory();
  if (!mem.lastSearches) mem.lastSearches = [];
  mem.lastSearches = [query, ...mem.lastSearches.filter(s => s !== query)].slice(0, 10);
  saveMemory(mem);
}

export function shouldSkipClarification(mem: UserMemory): boolean {
  return mem.queriesCount >= 5 && !!mem.preferredPriority;
}

export function getClarificationReductionLevel(mem: UserMemory): "none" | "partial" | "full" {
  if (mem.totalDecisions >= 8 && !!mem.preferredPriority) return "full";
  if (mem.totalDecisions >= 4 && !!mem.preferredPriority) return "partial";
  return "none";
}

export function getMemoryInsights(mem: UserMemory): string[] {
  const insights: string[] = [];

  if (mem.queriesCount >= 3 && mem.preferredPriority) {
    insights.push(`Prioritizing ${mem.preferredPriority} based on your history`);
  }
  if (mem.budgetPreference === "budget") {
    insights.push("Filtering for value-first options");
  }
  if (mem.budgetPreference === "premium") {
    insights.push("Showing premium options first");
  }
  if (mem.deliveryPreference === "fastest") {
    insights.push("Prioritizing fastest delivery routes");
  }
  if (mem.localPreference === "local") {
    insights.push("Showing local sellers first");
  }
  if (mem.primaryUseCase) {
    insights.push(`Optimized for ${mem.primaryUseCase} use`);
  }

  return insights;
}

export function updateGeoMemory(country: string, city?: string): void {
  const mem = loadMemory();
  mem.detectedCountry = country;
  if (city) mem.detectedCity = city;
  saveMemory(mem);
}

// Verify storage integrity
export function verifyMemoryIntegrity(): boolean {
  const mem = loadMemory();
  return mem.lastUpdated !== getDefault().lastUpdated || mem.queriesCount > 0;
}
